/**
 * HomePage Component
 * 
 * Compact landing page showing:
 * 1. Top 6 Trends (Quick access)
 * 2. Recent News Preview
 * 3. Quick Links to main sections
 * 
 * Full trends functionality is now in /trends (TrendsPage).
 * Refactored to shadcn/ui + Tailwind.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import {
  TrendingUp,
  Newspaper,
  ArrowRight,
  RefreshCw,
  Search,
  MessageSquare,
  BarChart,
  Clock,
  ChevronRight,
  ExternalLink,
  Star,
  Link2
} from 'lucide-react';
import GeoFlag from '../../components/GeoFlag';
import RegionCombobox from '../../components/RegionCombobox';
import HorizontalScroll from '../../components/HorizontalScroll';

import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { ApiServices, newsService } from '../../services/ApiServices';
import { cn } from '../../lib/utils';
import { useAuth } from '../../context/AuthContext';
import { NEWS_CATEGORY_LABELS, NEWS_TAG_LABELS } from '../../constants/newsClassification';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';

/** Shorter so card titles stay one line in narrow layouts */
const TREND_TITLE_MAX_LENGTH = 22;

function truncateTrendTitle(title) {
  if (!title || title.length <= TREND_TITLE_MAX_LENGTH) return title;
  return `${title.slice(0, TREND_TITLE_MAX_LENGTH)}…`;
}

function newsCountLabel(n) {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return `${n} новость`;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return `${n} новости`;
  return `${n} новостей`;
}

const TOP_TRENDS_LIMIT = 20;
const TOP_CELEBS_LIMIT = 10;

/** Both home blocks count the last 48 hours (/trends/hot/, /celebrities/hot/) - the full list opens with the same period */
const HOME_BLOCKS_DATE = 'now 2-d';

function trendsListLink(geo, category = null) {
  const params = new URLSearchParams();
  if (category) params.set('category', category);
  params.set('date', HOME_BLOCKS_DATE);
  if (geo && geo !== 'all') params.set('geo', geo);
  return `/trends?${params.toString()}`;
}

/** Country flags on a card: the first ones as badges, the rest in "+N" with a tooltip */
const MAX_CARD_GEOS = 3;

const GeoFlagList = ({ geos }) => {
  // "+1" takes the same room as one more flag, so it only appears from two hidden countries up
  const visibleCount = geos.length > MAX_CARD_GEOS + 1 ? MAX_CARD_GEOS : geos.length;
  const shown = geos.slice(0, visibleCount);
  const rest = geos.slice(visibleCount);
  return (
    <>
      {shown.map((geo) => (
        <GeoFlag key={geo} geo={geo} />
      ))}
      {rest.length > 0 && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge variant="outline" className="text-[11px] font-normal py-0.5 h-5">
                +{rest.length}
              </Badge>
            </TooltipTrigger>
            <TooltipContent side="top">{rest.join(', ')}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </>
  );
};

const TrendCard = ({ trend, rank, onClick, showGeo, showState = true }) => (
  <Card
    className="cursor-pointer transition-all hover:shadow-lg hover:-translate-y-1 overflow-hidden group h-[176px] flex flex-col"
    onClick={onClick}
  >
    <CardContent className="p-5 flex flex-col h-full">
      <div className="flex items-start justify-between gap-3 mb-3">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <h3 className="font-bold text-base leading-tight group-hover:text-primary transition-colors min-w-0 truncate">
                {truncateTrendTitle(trend.query)}
              </h3>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-[320px]">
              {trend.query}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <Badge variant={rank <= 3 ? "default" : "secondary"} className="shrink-0 font-bold">
          #{rank}
        </Badge>
      </div>

      <div className="flex flex-wrap gap-2 mb-auto">
        {showState && trend.state && (
          <Badge variant="outline" className="text-xs font-normal">
            {trend.state}
          </Badge>
        )}
        <Badge variant="secondary" className="text-xs font-normal">
          {trend.domain || 'Общее'}
        </Badge>
        {typeof trend.correlated_news_count === 'number' && trend.correlated_news_count > 0 && (
          <Badge variant="secondary" className="bg-sky-500/90 text-sky-50 text-xs font-normal">
            {newsCountLabel(trend.correlated_news_count)}
          </Badge>
        )}
        {showGeo && (
          <GeoFlagList geos={Array.isArray(trend.geos) && trend.geos.length > 0 ? trend.geos : (trend.geo ? [trend.geo] : [])} />
        )}
      </div>

      <div className="flex items-center justify-between text-xs text-muted-foreground mt-auto">
        <div className="flex items-center gap-1">
          <TrendingUp className="h-3.5 w-3.5 text-green-500" />
          <span className="font-medium text-foreground">
            {trend.search_count || '10 тыс.+'}
          </span>
        </div>
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity text-primary">
          Исследовать <ArrowRight className="h-3 w-3" />
        </div>
      </div>
    </CardContent>
  </Card>
);

const STRONG_CORRELATION_SCORE = 0.9

const NewsCard = ({ article, showGeo, navigate }) => {
  const [trendTooltipOpen, setTrendTooltipOpen] = useState(false)
  const trendHoverTimeoutRef = useRef(null)
  const trendBadgeRef = useRef(null)
  const leaveTimeoutRef = useRef(null)

  const formatDate = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleString('ru-RU', {
        day: 'numeric',
        month: 'long',
        hour: '2-digit',
        minute: '2-digit'
      })
    } catch (e) {
      return dateStr
    }
  }

  const displayTitle = article.title_ru || article.title

  const handleCardClick = (e) => {
    const target = e.target
    const isTrendBadgeArea = target?.closest?.('[data-trend-badge-area]')
    if (isTrendBadgeArea) {
      e.stopPropagation()
      e.preventDefault()
      return
    }
    window.open(article.link, '_blank')
  }

  const handleTrendBadgeMouseEnter = () => {
    if (leaveTimeoutRef.current) clearTimeout(leaveTimeoutRef.current)
    trendHoverTimeoutRef.current = setTimeout(() => setTrendTooltipOpen(true), 200)
  }
  const handleTrendBadgeMouseLeave = () => {
    if (trendHoverTimeoutRef.current) clearTimeout(trendHoverTimeoutRef.current)
    leaveTimeoutRef.current = setTimeout(() => setTrendTooltipOpen(false), 100)
  }
  const handleTrendTooltipMouseEnter = () => {
    if (leaveTimeoutRef.current) clearTimeout(leaveTimeoutRef.current)
    setTrendTooltipOpen(true)
  }
  useEffect(() => () => {
    if (trendHoverTimeoutRef.current) clearTimeout(trendHoverTimeoutRef.current)
    if (leaveTimeoutRef.current) clearTimeout(leaveTimeoutRef.current)
  }, [])

  return (
    <Card className="overflow-hidden flex flex-col h-full hover:shadow-md transition-shadow cursor-pointer group" onClick={handleCardClick}>
      <div className="aspect-video relative bg-muted overflow-hidden">
        {article.image ? (
          <img
            src={article.image}
            alt={displayTitle}
            referrerPolicy="no-referrer"
            className="object-cover w-full h-full transition-transform group-hover:scale-105"
          />
        ) : (
          <div className="flex items-center justify-center w-full h-full">
            <Newspaper className="h-12 w-12 text-muted-foreground/30" />
          </div>
        )}
        <div className="absolute top-2 left-2 right-2 flex items-start justify-between gap-2">
          <div className="flex flex-col gap-1 shrink-0">
            <Badge variant="secondary" className="bg-background/80 backdrop-blur-sm w-fit">
              {article.source_name}
            </Badge>
            {showGeo && article.geo && (
              <GeoFlag geo={article.geo} className="bg-background/80 backdrop-blur-sm w-fit" />
            )}
          </div>
          <div className="flex flex-col items-end gap-1 shrink-0">
          {article.correlated_trends_count > 0 && (
            <div
              data-trend-badge-area
              ref={trendBadgeRef}
              className="shrink-0 relative"
              onMouseEnter={handleTrendBadgeMouseEnter}
              onMouseLeave={handleTrendBadgeMouseLeave}
            >
              <Badge className="bg-sky-500/90 text-sky-50 backdrop-blur-sm text-[10px] cursor-pointer hover:bg-sky-500">
                {article.correlated_trends_count === 1
                  ? 'Связан с трендом'
                  : `Связан с ${article.correlated_trends_count} трендами`}
              </Badge>
              {trendTooltipOpen && trendBadgeRef.current && typeof document !== 'undefined' && createPortal(
                <div
                  id={'trend-tooltip-' + article.id}
                  className="fixed z-[99999] min-w-[180px] max-w-[280px] rounded-md border border-border bg-popover p-1 shadow-xl"
                  style={{
                    top: trendBadgeRef.current.getBoundingClientRect().bottom + 4,
                    left: Math.max(8, trendBadgeRef.current.getBoundingClientRect().right - 200),
                  }}
                  onMouseEnter={handleTrendTooltipMouseEnter}
                  onMouseLeave={handleTrendBadgeMouseLeave}
                >
                  {Array.isArray(article.correlated_trends) && article.correlated_trends.map((t) => {
                    const targetUrl = `/trend/${encodeURIComponent(t.query)}?geo=${t.geo || ''}`
                    const isStrong = (t.score ?? 0.6) >= STRONG_CORRELATION_SCORE
                    return (
                      <Link
                        key={t.id}
                        to={targetUrl}
                        className="flex items-center gap-2 cursor-pointer rounded-sm px-2 py-1.5 text-sm hover:bg-accent truncate"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {isStrong ? (
                          <Star className="h-3.5 w-3.5 shrink-0 fill-amber-400 text-amber-400" />
                        ) : (
                          <Link2 className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                        )}
                        <span className="truncate">{t.query} {t.geo ? `(${t.geo})` : ''}</span>
                      </Link>
                    )
                  })}
                </div>,
                document.body
              )}
            </div>
          )}
          {article.category && (
            <Badge className="bg-primary/90 text-primary-foreground backdrop-blur-sm text-[10px]">
              {NEWS_CATEGORY_LABELS[article.category] || article.category}
            </Badge>
          )}
          {Array.isArray(article.tags) && article.tags
            .filter(t => t !== 'other')
            .map(t => (
              <Badge key={t} variant="secondary" className="bg-background/80 backdrop-blur-sm text-[10px] px-1.5 py-0 h-4">
                {NEWS_TAG_LABELS[t] || t}
              </Badge>
            ))
          }
          </div>
        </div>
      </div>
      <CardContent className="p-4 flex-grow flex flex-col">
        <div className="flex-grow">
          <h3 className="font-semibold leading-tight mb-2 line-clamp-2 group-hover:text-primary transition-colors text-sm">
            {displayTitle}
          </h3>
          <p className="text-xs text-muted-foreground line-clamp-2 mb-4">
            {article.description}
          </p>
        </div>
        <div className="mt-auto pt-4 border-t flex items-center justify-between text-[10px] text-muted-foreground">
          <span className="flex items-center gap-1">
            <Clock className="h-3 w-3" />
            {formatDate(article.published_at)}
          </span>
          <span className="flex items-center gap-1 group-hover:text-primary transition-colors">
            Источник
            <ExternalLink className="h-3 w-3" />
          </span>
        </div>
      </CardContent>
    </Card>
  )
}

const NewsPreviewCard = () => (
  <Card className="overflow-hidden h-full">
    <div className="aspect-video bg-muted animate-pulse" />
    <CardContent className="p-4 space-y-3">
      <Skeleton className="h-4 w-3/4" />
      <div className="space-y-2">
        <Skeleton className="h-3 w-full" />
        <Skeleton className="h-3 w-full" />
      </div>
    </CardContent>
  </Card>
);

const RegionSelect = ({ value, onChange }) => (
  <RegionCombobox value={value} onChange={onChange} align="end" className="h-9 w-[220px] px-3" />
);

/** Home card row: arrows at the edges as in our trackers (HorizontalScroll) */
const DragScrollRow = ({ children }) => (
  <HorizontalScroll contentClassName="flex gap-1 pt-1 pb-4">
    {children}
  </HorizontalScroll>
);

const CardRowSkeleton = ({ count }) => (
  Array(count).fill(0).map((_, i) => (
    <Card key={i} className="flex-shrink-0 w-[246px] h-[176px]">
      <CardContent className="p-5 space-y-4">
        <div className="flex justify-between">
          <Skeleton className="h-6 w-1/2" />
          <Skeleton className="h-6 w-8" />
        </div>
        <Skeleton className="h-4 w-20" />
        <Skeleton className="h-4 w-full mt-4" />
      </CardContent>
    </Card>
  ))
);

const HomePage = () => {
  const navigate = useNavigate();
  const [topTrends, setTopTrends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedGeo, setSelectedGeo] = useState('all');
  const [selectedNewsGeo, setSelectedNewsGeo] = useState('all');
  const [latestNews, setLatestNews] = useState([]);
  const [newsLoading, setNewsLoading] = useState(true);
  const [topCelebs, setTopCelebs] = useState([]);
  const [celebsLoading, setCelebsLoading] = useState(true);
  const [celebsError, setCelebsError] = useState(null);
  const [selectedCelebGeo, setSelectedCelebGeo] = useState('all');
  const { user } = useAuth();

  const openTrend = (trend) => {
    const params = new URLSearchParams();
    if (trend.geo) params.append('geo', trend.geo);
    params.append('date', 'now 7-d');
    if (trend.state) params.append('category', trend.state.toLowerCase().replace(/\s+/g, '_'));
    if (trend.domain && trend.domain !== 'Other') params.append('domain', trend.domain);
    navigate(`/trend/${encodeURIComponent(trend.query)}?${params.toString()}`);
  };


  const fetchTopTrends = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await ApiServices.getHotTrends({
        geo: selectedGeo,
        limit: TOP_TRENDS_LIMIT
      });

      let trends = [];
      if (response?.trends) {
        trends = response.trends;
      } else if (response?.trending_searches) {
        trends = response.trending_searches;
      } else if (Array.isArray(response)) {
        trends = response;
      }

      const nonGarbage = trends.filter(t => !t.is_garbage);
      setTopTrends(nonGarbage);
    } catch (err) {
      console.error('Error fetching hot trends:', err);
      setError('Не удалось загрузить топ трендов');
    } finally {
      setLoading(false);
    }
  }, [selectedGeo]);

  const fetchLatestNews = useCallback(async () => {
    setNewsLoading(true);
    try {
      const data = await newsService.getHotNews({ geo: selectedNewsGeo });
      setLatestNews(data || []);
    } catch (err) {
      console.error('Error fetching hot news:', err);
    } finally {
      setNewsLoading(false);
    }
  }, [selectedNewsGeo]);

  const fetchTopCelebs = useCallback(async () => {
    setCelebsLoading(true);
    setCelebsError(null);
    try {
      const response = await ApiServices.getHotCelebrities({
        geo: selectedCelebGeo,
        limit: TOP_CELEBS_LIMIT
      });
      setTopCelebs(Array.isArray(response?.celebrities) ? response.celebrities : []);
    } catch (err) {
      console.error('Error fetching hot celebrities:', err);
      setCelebsError('Не удалось загрузить селеб');
    } finally {
      setCelebsLoading(false);
    }
  }, [selectedCelebGeo]);

  useEffect(() => {
    fetchTopTrends();
  }, [fetchTopTrends]);

  useEffect(() => {
    fetchTopCelebs();
  }, [fetchTopCelebs]);

  useEffect(() => {
    fetchLatestNews();
  }, [fetchLatestNews]);

  useEffect(() => {
    ApiServices.trackAuditEvent('page_view', 'homepage');
  }, []);

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl space-y-12">
      {/* Top Trends Section */}
      <section>
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
          <div className="space-y-1">
            <h2 className="text-2xl font-bold flex items-center gap-2">
              <TrendingUp className="h-6 w-6 text-primary" />
              Популярные тренды
            </h2>
            <p className="text-sm text-muted-foreground">Топ-{TOP_TRENDS_LIMIT} самых популярных запросов {selectedGeo === 'all' ? 'по всему миру' : `в регионе ${selectedGeo}`}</p>
          </div>

          <div className="flex items-center gap-2">
            <RegionSelect value={selectedGeo} onChange={setSelectedGeo} />

            <Button
              variant="outline"
              size="icon"
              className="h-9 w-9"
              onClick={() => fetchTopTrends(selectedGeo, true)}
              disabled={loading}
              title="Обновить тренды"
            >
              <RefreshCw className={cn("h-4 w-4", loading && "animate-spin")} />
            </Button>
            <Button asChild className="h-9">
              <Link to={trendsListLink(selectedGeo)}>
                Все тренды
                <ArrowRight className="h-4 w-4 ml-2" />
              </Link>
            </Button>
          </div>
        </div>

        {error && (
          <Alert variant="destructive" className="mb-6">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <DragScrollRow>
          {loading ? (
            <CardRowSkeleton count={8} />
          ) : topTrends.length > 0 ? (
            topTrends.map((trend, index) => (
              <div key={`${trend.query}-${index}`} className="flex-shrink-0 w-[246px]">
                <TrendCard
                  trend={trend}
                  rank={index + 1}
                  onClick={() => openTrend(trend)}
                  showGeo
                />
              </div>
            ))
          ) : (
            <div className="w-full py-12 text-center border rounded-lg bg-muted/20">
              <TrendingUp className="h-10 w-10 mx-auto text-muted-foreground mb-3 opacity-50" />
              <p className="text-muted-foreground">Данные о трендах в данный момент недоступны.</p>
            </div>
          )}
        </DragScrollRow>
      </section>

      {/* Top Celebrities Section */}
      <section>
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
          <div className="space-y-1">
            <h2 className="text-2xl font-bold flex items-center gap-2">
              <Star className="h-6 w-6 text-primary" />
              Популярные селебы
            </h2>
            <p className="text-sm text-muted-foreground">Топ-{TOP_CELEBS_LIMIT} знаменитостей в трендах {selectedCelebGeo === 'all' ? 'по всему миру' : `в регионе ${selectedCelebGeo}`}</p>
          </div>

          <div className="flex items-center gap-2">
            <RegionSelect value={selectedCelebGeo} onChange={setSelectedCelebGeo} />

            <Button
              variant="outline"
              size="icon"
              className="h-9 w-9"
              onClick={fetchTopCelebs}
              disabled={celebsLoading}
              title="Обновить селеб"
            >
              <RefreshCw className={cn("h-4 w-4", celebsLoading && "animate-spin")} />
            </Button>
            <Button asChild className="h-9">
              <Link to={trendsListLink(selectedCelebGeo, 'celebrity')}>
                Все селебы
                <ArrowRight className="h-4 w-4 ml-2" />
              </Link>
            </Button>
          </div>
        </div>

        {celebsError && (
          <Alert variant="destructive" className="mb-6">
            <AlertDescription>{celebsError}</AlertDescription>
          </Alert>
        )}

        <DragScrollRow>
          {celebsLoading ? (
            <CardRowSkeleton count={8} />
          ) : topCelebs.length > 0 ? (
            topCelebs.map((celeb, index) => (
              <div key={`${celeb.query}-${index}`} className="flex-shrink-0 w-[246px]">
                <TrendCard
                  trend={celeb}
                  rank={index + 1}
                  onClick={() => openTrend(celeb)}
                  showGeo
                  showState={false}
                />
              </div>
            ))
          ) : !celebsError && (
            <div className="w-full py-12 text-center border rounded-lg bg-muted/20">
              <Star className="h-10 w-10 mx-auto text-muted-foreground mb-3 opacity-50" />
              <p className="text-muted-foreground">За последние 48 часов знаменитостей в трендах нет.</p>
            </div>
          )}
        </DragScrollRow>
      </section>

      {/* News Preview Section */}
      <section>
        <div>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div>
              <h2 className="text-2xl font-bold flex items-center gap-2">
                <Newspaper className="h-6 w-6 text-primary" />
                Последние новости
              </h2>
              <p className="text-sm text-muted-foreground">Будьте в курсе последних событий {selectedNewsGeo === 'all' ? 'по всему миру' : `в регионе ${selectedNewsGeo}`}</p>
            </div>

            <div className="flex items-center gap-2">
              <RegionSelect value={selectedNewsGeo} onChange={setSelectedNewsGeo} />

              <Button
                variant="outline"
                size="icon"
                className="h-9 w-9"
                onClick={fetchLatestNews}
                disabled={newsLoading}
                title="Обновить новости"
              >
                <RefreshCw className={`h-4 w-4 ${newsLoading ? 'animate-spin' : ''}`} />
              </Button>
              <Button asChild className="h-9">
                <Link to="/news">
                  Все новости
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Link>
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {newsLoading ? (
              Array(12).fill(0).map((_, i) => (
                <NewsPreviewCard key={i} />
              ))
            ) : latestNews.length > 0 ? (
              latestNews.map((article) => (
                <NewsCard key={article.id} article={article} showGeo navigate={navigate} />
              ))
            ) : (
              <div className="col-span-full py-12 text-center border border-dashed rounded-lg bg-muted/10">
                <Newspaper className="h-10 w-10 mx-auto text-muted-foreground mb-3 opacity-30" />
                <h3 className="text-lg font-semibold">Последние новости</h3>
                <p className="text-sm text-muted-foreground">Свежие заголовки и обновления скоро появятся здесь.</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Tools Grid */}
      <section>
        <h2 className="text-2xl font-bold mb-6">Инструменты и аналитика</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          <Link to="/research" className="block h-full">
            <Card className="h-full hover:border-primary/50 transition-colors hover:bg-muted/30">
              <CardHeader>
                <Search className="h-8 w-8 mb-2 text-primary" />
                <CardTitle>Глубокое исследование</CardTitle>
                <CardDescription>
                  ИИ-анализ любой темы
                </CardDescription>
              </CardHeader>
            </Card>
          </Link>

          <Link to="/statistic" className="block h-full">
            <Card className="h-full hover:border-primary/50 transition-colors hover:bg-muted/30">
              <CardHeader>
                <BarChart className="h-8 w-8 mb-2 text-primary" />
                <CardTitle className="flex items-center gap-2">
                  Статистика рынка
                  <Badge variant="secondary" className="text-[10px] font-normal shrink-0">В разработке</Badge>
                </CardTitle>
                <CardDescription>
                  Подробные метрики и корреляции
                </CardDescription>
              </CardHeader>
            </Card>
          </Link>

          <Link to="/prompting" className="block h-full">
            <Card className="h-full hover:border-primary/50 transition-colors hover:bg-muted/30">
              <CardHeader>
                <MessageSquare className="h-8 w-8 mb-2 text-primary" />
                <CardTitle className="flex items-center gap-2">
                  ИИ-промпты
                  <Badge variant="secondary" className="text-[10px] font-normal shrink-0">В разработке</Badge>
                </CardTitle>
                <CardDescription>
                  Генерация контента на основе трендов
                </CardDescription>
              </CardHeader>
            </Card>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HomePage;